import { getIn } from 'formik';

import type { Dispatch } from '@reduxjs/toolkit';
import type { FormikErrors, FormikTouched } from 'formik';
import type { FButtonMenuOption } from '../../../../components/baseV2';
import type {
  QuestionnaireFormSnapshot,
  QuestionnaireFieldValue,
  QuestionnaireTabStatusEntry,
  TabStatusSource,
  UpdateTabStatusAction,
} from './types';
import type { TabConfig, TabStatus, TabStatusMap } from '../types';

// Walks arbitrary Formik values/errors/touched trees; narrows at runtime.

export function hasFieldValue(value: QuestionnaireFieldValue): boolean {
  if (value == null || value === false || value === '') {
    return false;
  }

  if (Array.isArray(value)) {
    return value.some(hasFieldValue);
  }

  if (value instanceof Object) {
    return Object.values(value).some(hasFieldValue);
  }

  return true;
}

export function hasTouchedError<TValues extends object>(
  errors: FormikErrors<TValues>,
  touched: FormikTouched<TValues>,
): boolean {
  return Object.keys(errors).some(
    (fieldName) => Boolean(getIn(errors, fieldName)) && Boolean(getIn(touched, fieldName)),
  );
}

export function shouldTouchAllErrorFields<TValues extends object>(
  errors: FormikErrors<TValues>,
  touched: FormikTouched<TValues>,
  shouldShowAllErrors: boolean,
): boolean {
  if (!hasFieldValue(errors)) {
    return false;
  }

  return shouldShowAllErrors || hasTouchedError(errors, touched);
}

/** Same reveal gate as tab-status errors: submit, any touched field, or saved values. */
export function shouldRevealQuestionnaireErrors<TValues extends object>(options: {
  submitCount: number;
  touched: FormikTouched<TValues>;
  hasSavedValues: boolean;
}): boolean {
  return options.submitCount > 0 || hasFieldValue(options.touched) || options.hasSavedValues;
}

export function areAllFieldsTouched<TValues extends object>(
  values: TValues,
  touched: FormikTouched<TValues>,
): boolean {
  return Object.keys(values).every((fieldName) => getIn(touched, fieldName) === true);
}

function getTabStatus<TValues extends object>(statusSource: TabStatusSource<TValues>): TabStatus {
  const hasErrors = hasFieldValue(statusSource.errors) || !statusSource.extraRequirementMet;
  // The error icon only surfaces once this specific tab has something to flag — a saved field,
  // a field the user touched, or a submit attempt. A tab the user hasn't entered anything into
  // stays neutral, even when other tabs have data.
  const shouldShowError = shouldRevealQuestionnaireErrors({
    submitCount: statusSource.submitCount,
    touched: statusSource.touched,
    hasSavedValues: statusSource.hasSavedValues,
  });
  const shouldShowSuccess = !hasErrors && (statusSource.dirty || shouldShowError);

  return {
    error: shouldShowError && hasErrors,
    success: shouldShowSuccess,
  };
}

export function getTabStatusFromSnapshot<TValues>(
  snapshot: QuestionnaireFormSnapshot<TValues>,
): TabStatus {
  return getTabStatus({
    errors: snapshot.errors,
    touched: snapshot.touched,
    dirty: snapshot.dirty,
    hasSavedValues: snapshot.hasSavedValues,
    submitCount: snapshot.submitCount,
    extraRequirementMet: snapshot.extraRequirementMet,
  });
}

export function isEqualTabStatus(first: TabStatus, second: TabStatus): boolean {
  return first.error === second.error && first.success === second.success;
}

export function buildQuestionnaireTabOptions<TTabId extends string>(
  tabConfigs: ReadonlyArray<TabConfig<TTabId>>,
  statuses: TabStatusMap<TTabId>,
  basePath: string,
  decorate?: (tabId: TTabId) => { loading?: boolean; error?: boolean },
): ReadonlyArray<FButtonMenuOption> {
  return tabConfigs.map((tabConfig) => {
    const decoration = decorate?.(tabConfig.id);

    return {
      value: tabConfig.id,
      label: tabConfig.label,
      to: `${basePath}/${tabConfig.path}`,
      // A decorator-supplied error (e.g. a pending insurance auto-fill review) surfaces as the
      // standard error indicator alongside the validation-derived error.
      error: statuses[tabConfig.id].error || (decoration?.error ?? false),
      success: statuses[tabConfig.id].success,
      loading: decoration?.loading ?? false,
    };
  });
}

/** Clears status icons for a completed (read-only) questionnaire's tab menu. */
export function withoutQuestionnaireTabStatusIcons(
  options: ReadonlyArray<FButtonMenuOption>,
): ReadonlyArray<FButtonMenuOption> {
  return options.map((option) => ({
    ...option,
    error: false,
    success: false,
    loading: false,
  }));
}

export function initializeTabStatuses<TTabId extends string>(
  entries: ReadonlyArray<QuestionnaireTabStatusEntry<TTabId>>,
  config: {
    updateTabStatus: UpdateTabStatusAction<TTabId>;
    dispatch: Dispatch;
  },
): void {
  entries.forEach(
    ({ tabId, values, schema, extraRequirementMet = true, hasNonFormikSavedValues = false }) => {
      const isComplete = extraRequirementMet && schema.isValidSync(values, { abortEarly: false });
      // Only flag an incomplete tab once at least one of its own fields has been saved, so a
      // tab the user never opened stays neutral instead of erroring the moment they start elsewhere.
      // Saved data living outside Formik (selection lists, uploaded documents) counts too, so a tab
      // whose only saved data is non-Formik doesn't stay neutral until it is first opened.
      const hasSavedValues = hasFieldValue(values) || hasNonFormikSavedValues;

      config.dispatch(
        config.updateTabStatus({
          tabId,
          status: { error: !isComplete && hasSavedValues, success: isComplete },
        }),
      );
    },
  );
}
